import { z } from "zod";

/**
 * Намерение «озвучка заказана из заготовки, и её запись ещё не приложена»
 * (`docs/tts-in-plan-design.md`, разделы 5.2, 5.5, 5.6 и 7).
 *
 * ⚠️ **Это место встречи ТРЁХ поверхностей, а не памятка окна.** Кто что пишет:
 *
 * | Поле | Пишет | Когда |
 * |---|---|---|
 * | `userId`, `jobId`, `planId`, `planTitle`, `at`, `draftMark` | окно (`addPending`) | при создании задачи |
 * | `fileId` | баннер (`settlePendingDone`) | когда увидел `done` |
 * | `placedAt` | редактор заготовки (`markPendingPlaced`) | в тот же миг, когда дописал слайд |
 * | `blocked` | редактор, а `gone` баннер (`markPendingBlocked`) | когда поставить не смог или не подтвердил |
 *
 * Снимает запись `dropPending`: редактор в `.then` того сохранения, которое
 * несло слайд, и «Понятно» на баннере. ⚠️ Запись с `blocked` редактор не снимает
 * никогда: снять её значит погасить единственную поверхность, которая может
 * назвать причину.
 *
 * ⚠️ **У записи есть владелец, и чужая запись невидима и неприкосновенна.**
 * Хранилище живёт в браузере, а не в сессии: выход из аккаунта его не чистит,
 * и на общем компьютере следующий человек (в том числе ученик) иначе получил бы
 * чанк озвучки и название чужой заготовки. Хуже того, под чужой сессией ручка
 * задачи ответила бы «нет такой», а список заготовок не нашёл бы нашу, и
 * баннер навсегда испортил бы запись владельца причиной `gone`. Поэтому каждая
 * функция ниже берёт `userId` и читает и правит ТОЛЬКО записи с ним; чужие
 * лежат нетронутыми, пока их не уберёт срок жизни.
 *
 * ⚠️ **Незнакомые поля записи переживают пересборку списка** (`.loose()`): иначе
 * вкладка со старым бандлом, поправив свою запись, срезала бы поля, которые
 * дописал новый бандл, и в соседних записях тоже.
 *
 * ⚠️ **Гонка «прочитать, поправить, записать» из двух вкладок принята.**
 * Единственный вредный исход: правка одной вкладки теряется, и обещание слайда
 * ломается молча, а файл при этом лежит в материалах.
 *
 * ⚠️ **Модуль живёт в стартовом чанке**, потому что его читает каркас кабинета
 * (`features/shell/TtsProgressGate.tsx`). Поэтому импортов здесь ровно один,
 * `zod`, который в стартовом чанке и так есть: ни слов, ни схем, ни ручек
 * озвучки, ни одной строки из `features/tts` (раздел 5.3; замок на это стоит
 * в сборке, раздел 11). Схема задачи из `@edu/shared` сюда тоже не едет:
 * записи нужны идентификаторы, а не форма ответа сервера.
 *
 * ⚠️ **Разбор схемой с выбрасыванием непрошедшего, и цена ошибки тут выше, чем у
 * черновика.** Черновик читает ленивый экран озвучки, а намерение читает
 * каркас, то есть незащищённый разбор уронил бы весь кабинет. Строка в
 * хранилище могла остаться от прошлого выпуска, от чужого расширения или от
 * правки руками. Выбрасывается каждая непрошедшая ЗАПИСЬ по отдельности, а не
 * весь список: одна битая запись не должна гасить соседнюю живую.
 *
 * ⚠️ **Каждое обращение к хранилищу обёрнуто**, как в `browserDraftStore`: при
 * запрещённых сайтовых данных сам доступ к `localStorage` бросает.
 */

/** Ключ списка намерений, рядом с черновиками озвучки. Значение не меняется никогда. */
export const TTS_PENDING_KEY = "edu.tts.pending";

/**
 * Ключ черновика окна озвучки в заготовке (раздел 7).
 *
 * ⚠️ **Живёт здесь, а не в `features/tts/tts-store.ts`**, хотя черновик читает
 * и пишет тот модуль: стирает черновик окна на `done` этот модуль, а импорт из
 * `features/tts` в стартовый чанк запрещён (раздел 5.3). Второй литерал той же
 * строки разъехался бы с первым на первой же правке, поэтому `tts-store.ts`
 * берёт значение отсюда и отдаёт его дальше под прежним именем.
 *
 * ⚠️ Значение строки не меняется никогда: под ним лежат живые черновики.
 */
export const PLAN_DRAFT_KEY = "edu.tts.draft.plan";

/**
 * Срок жизни записи: семь суток от заказа. Короче любого серверного срока уборки
 * задач, иначе баннер однажды спросил бы про задачу, которой нет (5.5).
 */
export const PENDING_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Почему запись не встала слайдом. Список закрытый (5.2): других входных данных,
 * кроме записи, у баннера нет, и причину, которой нет в списке, сказать некому.
 */
export const PendingBlocked = z.enum(["ceiling", "gone", "unsaved", "lost"]);
export type PendingBlocked = z.infer<typeof PendingBlocked>;

const PendingEntry = z
  .object({
    /**
     * Кто заказал. Обязательное: запись без владельца разбор выбрасывает как чужую
     * форму, потому что показать её некому.
     */
    userId: z.string().uuid(),
    jobId: z.string().uuid(),
    planId: z.string().uuid(),
    planTitle: z.string(),
    /** Когда заказали, мс от эпохи. От него считается срок жизни. */
    at: z.number().int().nonnegative(),
    /**
     * Отпечаток черновика окна на момент заказа (`draftMark`). `null`, если
     * черновика не было. Стирать на `done` можно только ТОТ черновик, из которого
     * заказывали: следующий заказ уже набран поверх.
     */
    draftMark: z.string().nullable().default(null),
    /** Готовый файл. Пишет баннер на `done`: окно к этому мигу закрыто. */
    fileId: z.string().uuid().nullable().default(null),
    /** Когда редактор дописал слайд. Событие ставится один раз за жизнь записи. */
    placedAt: z.number().int().nonnegative().nullable().default(null),
    blocked: PendingBlocked.nullable().default(null),
  })
  .loose();
export type PendingEntry = z.infer<typeof PendingEntry>;

/** Часть `Storage`, которой мы пользуемся. Параметром ради проверяемости. */
export interface PendingStore {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
}

/**
 * Хранилище браузера либо `null`. Обращение к самому свойству в try: при
 * запрещённых сайтовых данных бросает именно оно.
 */
export function browserPendingStore(): PendingStore | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readKey(store: PendingStore | null, key: string): string | null {
  if (store === null) return null;
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
}

/** Строка списка как она лежит в хранилище. Снимок для `useSyncExternalStore`. */
export function readPendingRaw(store: PendingStore | null): string | null {
  return readKey(store, TTS_PENDING_KEY);
}

/**
 * Отпечаток строки черновика: FNV-1a по кодам символов плюс длина. Не защита,
 * а сравнение «тот же текст или уже другой», поэтому простого хеша хватает.
 */
export function draftMark(raw: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < raw.length; i += 1) {
    hash ^= raw.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${raw.length}:${hash.toString(16)}`;
}

/**
 * Разбор строки списка, ВСЕ владельцы: битая строка даёт пустой список, битая
 * запись выбрасывается, просроченная тоже, а повтор `jobId` оставляет первую.
 * Показывать отсюда нельзя ничего: для показа есть `ownPending`.
 */
function parseAll(raw: string | null, now: number): PendingEntry[] {
  if (raw === null) return [];
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(data)) return [];
  const out: PendingEntry[] = [];
  for (const item of data) {
    const parsed = PendingEntry.safeParse(item);
    if (!parsed.success) continue;
    if (!alive(parsed.data, now)) continue;
    if (out.some((e) => e.jobId === parsed.data.jobId)) continue;
    out.push(parsed.data);
  }
  return out;
}

/** Записи этого человека из строки списка. `userId === null` не видит ничего. */
export function ownPending(raw: string | null, userId: string | null, now: number): PendingEntry[] {
  if (userId === null) return [];
  return parseAll(raw, now).filter((e) => e.userId === userId);
}

export function readPending(
  store: PendingStore | null,
  userId: string | null,
  now: number,
): PendingEntry[] {
  return ownPending(readPendingRaw(store), userId, now);
}

function alive(entry: PendingEntry, now: number): boolean {
  return now - entry.at < PENDING_TTL_MS;
}

/**
 * Подписчики ЭТОЙ вкладки.
 *
 * ⚠️ **Доставка изменений двумя половинами, и каждая закрывает своё.** Событие
 * `storage` в ПИШУЩЕЙ вкладке не срабатывает вовсе, то есть без своей подписки
 * привратник не увидел бы того, что окно этой же вкладки только что записало.
 * А без слушателя `storage` (`subscribePending`) чужая вкладка не увидела бы
 * запись никогда.
 */
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of [...listeners]) listener();
}

/** Где слушать событие `storage`. Параметром ради проверяемости без DOM. */
export interface StorageEventSource {
  addEventListener: (type: "storage", listener: (event: Event) => void) => void;
  removeEventListener: (type: "storage", listener: (event: Event) => void) => void;
}

/**
 * Подписка на изменения списка, своей вкладки и чужих. Возвращает отписку.
 *
 * `key === null` у события `storage` означает `localStorage.clear()` в соседней
 * вкладке: список при этом тоже пропал, и знать об этом надо.
 */
export function subscribePending(
  listener: () => void,
  source: StorageEventSource | null,
): () => void {
  listeners.add(listener);
  const onStorage = (event: Event) => {
    const key = (event as Event & { key?: string | null }).key;
    if (key === TTS_PENDING_KEY || key === null) listener();
  };
  source?.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    source?.removeEventListener("storage", onStorage);
  };
}

/**
 * Записать список целиком (все владельцы). Просроченное выбрасывается здесь, на
 * записи, а не на чтении: чтение из рендера, пишущее в хранилище, будило бы
 * подписчиков из рендера. Пустой список стирает ключ.
 *
 * Отказ записи глотается намеренно, как у черновика; окно узнаёт о нём чтением
 * обратно (`addPending`).
 */
function writeAll(store: PendingStore | null, list: PendingEntry[], now: number): void {
  if (store === null) return;
  const kept = list.filter((e) => alive(e, now));
  try {
    if (kept.length === 0) store.removeItem(TTS_PENDING_KEY);
    else store.setItem(TTS_PENDING_KEY, JSON.stringify(kept));
  } catch {
    // Переполненное хранилище и запрет записи: сказать тут нечего
  }
  notify();
}

function mine(entry: PendingEntry, userId: string, jobId: string): boolean {
  return entry.userId === userId && entry.jobId === jobId;
}

/** Поправить СВОЮ запись. Чужие записи списка переписываются как были. */
function updateOwn(
  store: PendingStore | null,
  userId: string,
  jobId: string,
  now: number,
  change: (entry: PendingEntry) => PendingEntry,
): void {
  const all = parseAll(readPendingRaw(store), now);
  if (!all.some((e) => mine(e, userId, jobId))) return;
  writeAll(
    store,
    all.map((e) => (mine(e, userId, jobId) ? change(e) : e)),
    now,
  );
}

/**
 * Окно: записать намерение при создании задачи и ПРОЧИТАТЬ его обратно (5.6).
 *
 * Возвращает `true`, только если запись читается после записи: обещание «запись
 * встанет новым слайдом» висит на этом ответе, а не на наличии хранилища.
 * Переполненная квота отдаёт хранилище и молча теряет запись.
 *
 * ⚠️ Отпечаток черновика снимается ЗДЕСЬ, с того, что лежит под
 * `PLAN_DRAFT_KEY` в миг вызова. Значит окно зовёт эту функцию сразу после
 * создания задачи, до любой правки черновика.
 *
 * ⚠️ Запись с тем же `jobId` не перезаписывается: сервер на повтор того же
 * текста отдаёт уже идущую задачу, и затирать её `placedAt` значило бы
 * поставить слайд второй раз. Чужая запись с тем же `jobId` (невозможная по
 * построению) тоже не трогается, и ответ тогда `false`.
 *
 * ⚠️ `fromDraft === false` значит «заказ ушёл НЕ из черновика окна»: окно
 * открыли с текстом аргументом (`initialText`, раздел 15) и до заказа его не
 * правили, то есть под `PLAN_DRAFT_KEY` лежит другой, более ранний текст.
 * Отпечаток тогда не снимается вовсе: снятый, он совпал бы с тем ранним
 * текстом, и `done` стёр бы набранное, которое никто не заказывал.
 */
export function addPending(
  store: PendingStore | null,
  order: { userId: string; jobId: string; planId: string; planTitle: string },
  now: number,
  fromDraft = true,
): boolean {
  const all = parseAll(readPendingRaw(store), now);
  if (!all.some((e) => e.jobId === order.jobId)) {
    const draft = fromDraft ? readKey(store, PLAN_DRAFT_KEY) : null;
    const entry = PendingEntry.safeParse({
      ...order,
      at: now,
      draftMark: draft === null ? null : draftMark(draft),
    });
    if (!entry.success) return false;
    writeAll(store, [...all, entry.data], now);
  }
  return readPending(store, order.userId, now).some((e) => e.jobId === order.jobId);
}

/** Ключ пробной записи `canRecordPending`. Живёт миг и стирается тем же вызовом. */
export const TTS_PROBE_KEY = "edu.tts.probe";

/**
 * Окно, при открытии: ляжет ли запись намерения, если заказать сейчас (5.6).
 *
 * Первая строка окна обещает слайд или говорит «поставить руками», и сказать это
 * надо ДО нажатия, а `addPending` отвечает только после создания задачи.
 * Поэтому проба тем же способом, каким `addPending` подтверждает запись: записать
 * и прочитать обратно. Отдельным ключом, а не списком намерений: запись в список
 * будила бы подписчиков баннера во всех вкладках ради ничего.
 *
 * ⚠️ Проба не гарантия: запись намерения длиннее пробы, и на самой кромке
 * квоты проба пройдёт, а запись нет. Тогда окно узнаёт об этом из ответа
 * `addPending` и следующее открытие говорит вторую строку.
 */
export function canRecordPending(store: PendingStore | null): boolean {
  if (store === null) return false;
  try {
    store.setItem(TTS_PROBE_KEY, "1");
    const back = store.getItem(TTS_PROBE_KEY);
    store.removeItem(TTS_PROBE_KEY);
    return back === "1";
  } catch {
    return false;
  }
}

/**
 * Баннер: задача дошла до `done`. Дописывает `fileId` и стирает черновик окна
 * (раздел 7): пока задача не готова, набранное держится, потому что окно
 * закрылось сразу и упавшая задача иначе оставила бы человека ни с чем.
 *
 * ⚠️ Черновик стирается, только если он ТОТ ЖЕ, из которого заказывали
 * (отпечаток совпал). Живых задач бывает три: заказал первую, набрал и заказал
 * вторую, первая готова. Стерев черновик без сверки, первая стёрла бы текст
 * второй, а упади вторая, человек остался бы ни с чем. И только при ПЕРВОЙ
 * дописке файла, а не на каждом показе готовой записи.
 */
export function settlePendingDone(
  store: PendingStore | null,
  userId: string,
  jobId: string,
  fileId: string,
  now: number,
): void {
  const entry = readPending(store, userId, now).find((e) => e.jobId === jobId);
  if (entry === undefined || entry.fileId !== null) return;
  updateOwn(store, userId, jobId, now, (e) => ({ ...e, fileId }));
  const draft = readKey(store, PLAN_DRAFT_KEY);
  if (entry.draftMark !== null && draft !== null && draftMark(draft) === entry.draftMark) {
    forgetPlanDraft(store);
  }
}

/** Редактор: слайд дописан. Ставится один раз, повтор ничего не меняет. */
export function markPendingPlaced(
  store: PendingStore | null,
  userId: string,
  jobId: string,
  now: number,
): void {
  updateOwn(store, userId, jobId, now, (e) => (e.placedAt === null ? { ...e, placedAt: now } : e));
}

/**
 * Записать причину, по которой слайда не будет. Первая причина остаётся: её
 * человек, возможно, уже прочёл.
 */
export function markPendingBlocked(
  store: PendingStore | null,
  userId: string,
  jobId: string,
  reason: PendingBlocked,
  now: number,
): void {
  updateOwn(store, userId, jobId, now, (e) => (e.blocked === null ? { ...e, blocked: reason } : e));
}

/** Снять СВОЮ запись: подтверждённое сохранение со слайдом или «Понятно». */
export function dropPending(
  store: PendingStore | null,
  userId: string,
  jobId: string,
  now: number,
): void {
  const all = parseAll(readPendingRaw(store), now);
  if (!all.some((e) => mine(e, userId, jobId))) return;
  writeAll(
    store,
    all.filter((e) => !mine(e, userId, jobId)),
    now,
  );
}

/**
 * Какую живую задачу подхватить экрану озвучки: первую, у которой в этом
 * браузере нет своего намерения.
 *
 * ⚠️ Задача, заказанная из окна заготовки, принадлежит баннеру: экран, открытый
 * следом, показал бы её второй панелью рядом с баннером, с двумя кнопками
 * «Остановить» и двумя рассказами про одну работу. Обратное правило стоит у
 * баннера: задача экрана в него не попадает, потому что намерения у неё нет.
 */
export function jobForScreen<T extends { jobId: string }>(
  jobs: readonly T[],
  own: readonly PendingEntry[],
): T | undefined {
  return jobs.find((job) => !own.some((e) => e.jobId === job.jobId));
}

/** Стереть черновик окна. Отказ хранилища глотается, как у `clearDraft`. */
function forgetPlanDraft(store: PendingStore | null): void {
  if (store === null) return;
  try {
    store.removeItem(PLAN_DRAFT_KEY);
  } catch {
    /* см. `writeAll` */
  }
}
